import { useState } from 'react';
import { chantiersApi } from '../../api/resources';
import { NumInput, _inp } from '../../components/ui/Inputs.jsx';
import { tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';

/**
 * Ce qui reste d'un achat fournisseur, retours au dépôt déduits.
 *
 * @param {object} achat ligne de matériel acheté chez le fournisseur
 * @param {object[]} lignes tout le matériel du chantier
 * @returns {number} la quantité qu'on peut encore rapporter
 */
export function resteDeLAchat(achat, lignes) {
  if (!achat || achat.source !== 'fournisseur' || !(+achat.qte > 0)) return 0;
  const rapporte = (lignes || [])
    .filter((m) => m.retourDe != null && m.retourDe === achat.id)
    .reduce((t, m) => t + Math.abs(+m.qte || 0), 0);
  return Math.max(0, Math.round(((+achat.qte || 0) - rapporte) * 1000) / 1000);
}

/**
 * « ↩️ Rapporter au dépôt » sous un achat fait chez un fournisseur.
 *
 * <p>Le reste revient au dépôt (le stock monte, l'article est créé s'il n'y
 * était pas) et sort du coût du chantier au prix payé. L'ouvrier, le patron et
 * le technicien s'en servent de la même façon.
 *
 * @param {object} props propriétés
 * @param {number} props.chantierId identifiant du chantier
 * @param {object} props.achat ligne de matériel acheté
 * @param {object[]} props.lignes tout le matériel du chantier
 * @param {number} [props.employeId] collègue servi par le magasinier
 * @param {Function} props.onFait reçoit le chantier renvoyé par le serveur
 * @returns {JSX.Element|null} le bouton, ou rien s'il ne reste rien
 */
export function RetourAchatDepot({ chantierId, achat, lignes, employeId, onFait }) {
  const [ouvert, setOuvert] = useState(false);
  const [quantite, setQuantite] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const reste = resteDeLAchat(achat, lignes);
  if (typeof achat?.id !== 'number' || reste <= 0) return null;

  const valide = async () => {
    const q = +quantite;
    if (!(q > 0) || q > reste) {
      setErreur(trf('Entre une quantité entre 0 et {0}.', reste));
      return;
    }
    setEnvoi(true);
    setErreur('');
    try {
      const chantier = await chantiersApi.rapporteAchat(chantierId, achat.id, {
        quantite: q,
        employeId: employeId || undefined,
      });
      setOuvert(false);
      setQuantite('');
      onFait?.(chantier);
    } catch (e) {
      setErreur(e?.message || tr("L'enregistrement a échoué."));
    } finally {
      setEnvoi(false);
    }
  };

  if (!ouvert) {
    return (
      <button
        type="button"
        onClick={() => setOuvert(true)}
        style={{
          marginTop: 5,
          fontSize: 9.5,
          fontWeight: 800,
          color: C.teal,
          background: C.tealL,
          border: 'none',
          borderRadius: 7,
          padding: '5px 9px',
          cursor: 'pointer',
        }}
      >
        {tr('↩️ Rapporter le reste au dépôt')}
      </button>
    );
  }
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ fontSize: 9, color: C.muted, marginBottom: 4 }}>
        {trf('Il reste {0} {1} à rapporter au dépôt.', reste, achat.u || '')}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <div style={{ width: 90 }}>
          <NumInput
            value={quantite}
            onChange={(e) => setQuantite(e.target.value)}
            placeholder={String(reste)}
            style={{ ..._inp, padding: '7px 9px' }}
          />
        </div>
        <button
          type="button"
          disabled={envoi}
          onClick={valide}
          style={{
            fontSize: 10,
            fontWeight: 800,
            color: '#fff',
            background: envoi ? C.muted : C.teal,
            border: 'none',
            borderRadius: 8,
            padding: '8px 11px',
            cursor: envoi ? 'not-allowed' : 'pointer',
          }}
        >
          {envoi ? tr('Enregistrement…') : tr('✓ Rapporter')}
        </button>
        <button
          type="button"
          onClick={() => {
            setOuvert(false);
            setErreur('');
          }}
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: C.muted,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          {tr('Annuler')}
        </button>
      </div>
      {erreur && (
        <div role="alert" style={{ fontSize: 10, color: C.red, marginTop: 4 }}>
          {erreur}
        </div>
      )}
    </div>
  );
}
